<script>
    import {onMount} from "svelte"
    import Spectrum from "./Spectrum.svelte"
    import ChromaticityDiagram from "./ChromaticityDiagram.svelte"
    import {XYZtoRGB} from "./color.js"

    let showXY = true
    let showCone = true
    let showCones = true
    let showChromaticityDiagram = true
    let showSpectralLocus = true
    let showSRGBGamut = true

    let nmStep = 10
    let minNm = 380
    let maxNm = 780

    let light
    let sCone, mCone, lCone

    let X = 0,
        Y = 0,
        Z = 0
    let x = 0,
        y = 0,
        z = 0

    let htmlColor = ""

    $: {
        x = X / (X + Y + Z)
        y = Y / (X + Y + Z)
        z = Z / (X + Y + Z)

        let Y2 = 0.8
        let X2 = (Y2 / y) * x
        let Z2 = (Y2 / y) * (1 - x - y)

        let rgbValues = XYZtoRGB(X, Y, Z)
        htmlColor = `rgb(${Math.round(rgbValues.r * 255)}, ${Math.round(
            rgbValues.g * 255,
        )}, ${Math.round(rgbValues.b * 255)})`
    }

    let points = {}

    $: {
        if (showSRGBGamut) {
            points["sRGB R"] = {x: 0.64, y: 0.33, Y: 0.2126}
            points["sRGB G"] = {x: 0.3, y: 0.6, Y: 0.7152}
            points["sRGB B"] = {x: 0.15, y: 0.06, Y: 0.0722}
            points["sRGB W"] = {x: 0.3127, y: 0.329, Y: 1}
        } else {
            points = Object.fromEntries(
                Object.entries(points).filter(
                    ([k, v]) => k.substring(0, 4) !== "sRGB",
                ),
            )
        }
    }

    function addCIEColorMatching() {
        for (let i = minNm; i <= maxNm; i += nmStep) {
            lCone[i] = xMatching(i)
            mCone[i] = yMatching(i)
            sCone[i] = zMatching(i)
        }
    }

    function xMatching(nm) {
        return (
            1.056 * g(nm, 599.8, 37.9, 31.0) +
            0.362 * g(nm, 442.0, 16.0, 26.7) -
            0.065 * g(nm, 501.1, 20.4, 26.2)
        )
    }

    function yMatching(nm) {
        return (
            0.821 * g(nm, 568.8, 46.9, 40.5) + 0.286 * g(nm, 530.9, 16.3, 31.1)
        )
    }

    function zMatching(nm) {
        return 1.217 * g(nm, 437, 11.8, 36) + 0.681 * g(nm, 459, 26, 13.8)
    }

    function g(x, my, s1, s2) {
        if (x < my) {
            return Math.exp((-0.5 * (x - my) ** 2) / s1 ** 2)
        } else {
            return Math.exp((-0.5 * (x - my) ** 2) / s2 ** 2)
        }
    }

    $: {
        points["light"] = {X: X, Y: Y, Z: Z}
    }

    $: {
        if (sCone) {
            for (let i = 450; i <= 630; i += nmStep) {
                if (showSpectralLocus) {
                    points[`${i} nm`] = monochromaticXYZ(i)
                } else {
                    points = Object.fromEntries(
                        Object.entries(points).filter(
                            ([k, v]) => k !== `${i} nm`,
                        ),
                    )
                }
            }
        }
    }

    let monochromaticXYZ
    $: {
        if (lCone && mCone && sCone) {
            monochromaticXYZ = (nm) => {
                return {X: lCone[nm], Y: mCone[nm], Z: sCone[nm]}
            }
            light = light
        }
    }

    function xyClick(event) {
        let x = event.detail.x
        let y = event.detail.y
        let Y = 0.5

        // x = x / (X + Y + Z)
        // y = y / (X + Y + Z)
        // z = 1 - x - y = z / (X + Y + Z)

        let X = (Y / y) * x
        let Z = (Y / y) * (1 - x - y)
        let rgb = XYZtoRGB(X, Y, Z)
        console.log(rgb)

        let r = 630
        let g = 530
        let b = 460

        for (let i = minNm; i <= maxNm; i += nmStep) {
            let intensity = 0
            if (i == r) {
                intensity = Math.max(rgb.r, 0)
            } else if (i == g) {
                intensity = Math.max(rgb.g, 0)
            } else if (i == b) {
                intensity = Math.max(rgb.b, 0)
            }
            light[i] = intensity
        }
    }

    onMount(() => {
        addCIEColorMatching()
    })
</script>

<main>
    <div id="container">
        <div class="column">
            <h2>Light</h2>
            <Spectrum bind:spectrum={light} monoXYZ={monochromaticXYZ} />
            <h2>Color perception</h2>
            <div id="htmlColor" style="background: {htmlColor}" />
            {#if showXY}
                <h2>Percentages</h2>
                <div id="percentages">
                    {#if isFinite(x)}
                        x: {Math.round(x * 100)}%, y: {Math.round(y * 100)}%, z: {Math.round(
                            z * 100,
                        )}%
                    {/if}
                </div>
            {/if}
            <h2>Options</h2>
            <input type="checkbox" bind:checked={showCone} />
            cone <input type="checkbox" bind:checked={showCones} />
            more cones<br />

            <input type="checkbox" bind:checked={showXY} />
            percentages<br />

            <input type="checkbox" bind:checked={showChromaticityDiagram} />
            chromaticity diagram<br />

            <input type="checkbox" bind:checked={showSpectralLocus} />
            spectral locus<br />

            <input type="checkbox" bind:checked={showSRGBGamut} />
            sRGB Gamut<br />

            <button on:click={() => addCIEColorMatching()}
                >CIE 1931 color matching functions</button
            >
        </div>
        <div class="column">
            {#if showCone || showCones}
                <h2>X cone</h2>
                <Spectrum
                    name="L cones"
                    bind:spectrum={lCone}
                    multiply={light}
                    bind:sum={X}
                />
            {/if}
            {#if showCones}
                <h2>Y cone</h2>
                <Spectrum bind:spectrum={mCone} multiply={light} bind:sum={Y} />
                <h2>Z cone</h2>
                <Spectrum bind:spectrum={sCone} multiply={light} bind:sum={Z} />
            {/if}
        </div>
        <div class="column">
            {#if showChromaticityDiagram}
                <h2>xy chromaticity diagram</h2>
                <ChromaticityDiagram {points} on:click={xyClick} />
            {/if}
        </div>
    </div>
    <div>
        <b>Instruction:</b> Draw on the light spectrum to change the incoming
        light. – Draw on the cone graphs to change them. – Draw on the
        chromaticity diagram to create an RGB mix. – Watch the
        <a href="/what-is-color/" target="_blank">talk</a>.
    </div>
</main>

<style>
    main {
        display: flex;
        flex-direction: column;
    }
    #container {
        display: flex;
    }
    #htmlColor {
        width: 18em;
        height: 5rem;
        margin-left: 1rem;
        border: 1px solid black;
    }
    .column {
        padding: 1rem;
    }
    #percentages {
        font-size: 200%;
    }
</style>
